import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateEmployee } from "@/services/employee.service";
import { Compensation } from "@/types/models";

const compensationSchema = z.object({
  startDate: z.string().min(1, "Start date is required"),
  endDate: z.string().min(1, "End date is required"),
  newSalary: z.string().min(1, "New salary is required"),
  payFrequency: z.string().min(1, "Pay frequency is required"),
  noticePeriodInDays: z.string().min(1, "Notice period is required"),
  notifyPeriodInDays: z.string().min(1, "Notify period is required"),
  salaryMode: z.string().min(1, "Salary mode is required"),
  note: z.string().optional(),
}).refine((data) => {
  const startDate = new Date(data.startDate);
  const endDate = new Date(data.endDate);
  return endDate > startDate;
}, {
  message: "End date must be after start date",
  path: ["endDate"],
});

type CompensationFormValues = z.infer<typeof compensationSchema>;

interface EditCompensationDialogProps {
  compensation: Compensation;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function EditCompensationDialog({
  compensation,
  open,
  onOpenChange,
  onSuccess,
}: EditCompensationDialogProps) {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    setValue,
    watch,
  } = useForm<CompensationFormValues>({
    resolver: zodResolver(compensationSchema),
  });

  // Helper function to format date for HTML date input
  const formatDateForInput = (date: any) => {
    if (!date) return '';
    if (typeof date === 'string') {
      if (date.includes('T')) {
        return new Date(date).toISOString().split('T')[0];
      }
      return date;
    }
    return new Date(date).toISOString().split('T')[0];
  };

  // Reset form when compensation changes
  useEffect(() => {
    if (compensation && open) {
      reset({
        startDate: formatDateForInput(compensation.startDate),
        endDate: formatDateForInput(compensation.endDate),
        newSalary: compensation.newSalary || "",
        payFrequency: compensation.payFrequency || "",
        noticePeriodInDays: compensation.noticePeriodInDays || "",
        notifyPeriodInDays: compensation.notifyPeriodInDays || "",
        salaryMode: compensation.salaryMode || "",
        note: compensation.note || "",
      });
    }
  }, [compensation, open, reset]);

  const onSubmit = async (data: CompensationFormValues) => {
    if (!compensation.employee) {
      toast({
        title: "Error",
        description: "Employee data not found",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);

      // Get the full employee data to access compensationInfo
      const { getEmployees } = await import("@/services/employee.service");
      const employees = await getEmployees();
      // Define a minimal interface for an employee object to satisfy type checking.
      // It needs an _id for comparison and compensationInfo for later access.
      interface EmployeeWithCompensationInfo {
        _id: string;
        compensationInfo: any[]; // Assuming compensationInfo is an array, potentially of 'any' type based on existing code
      }
      const employee = employees.find((emp: EmployeeWithCompensationInfo) => emp._id === compensation.employee._id);

      if (!employee || !employee.compensationInfo) {
        toast({
          title: "Error",
          description: "Employee or compensation data not found",
          variant: "destructive",
        });
        return;
      }

      // Find the compensation record to update by matching start and end dates
      const compensationIndex = employee.compensationInfo.findIndex(
        (comp: any) =>
          new Date(comp.startDate).getTime() === new Date(compensation.startDate).getTime() &&
          new Date(comp.endDate).getTime() === new Date(compensation.endDate).getTime()
      );

      if (compensationIndex === -1) {
        toast({
          title: "Error",
          description: "Compensation record not found",
          variant: "destructive",
        });
        return;
      }

      // Update the compensation record
      const updatedCompensationInfo = [...employee.compensationInfo];
      updatedCompensationInfo[compensationIndex] = {
        ...updatedCompensationInfo[compensationIndex],
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        newSalary: data.newSalary,
        payFrequency: data.payFrequency,
        noticePeriodInDays: data.noticePeriodInDays,
        notifyPeriodInDays: data.notifyPeriodInDays,
        salaryMode: data.salaryMode,
        note: data.note,
      };

      // Update the employee using the same API as user profile
      await updateEmployee(employee._id, {
        compensationInfo: updatedCompensationInfo,
      });

      toast({
        title: "Success",
        description: "Compensation updated successfully",
      });

      onSuccess();
    } catch (error) {
      console.error("Error updating compensation:", error);
      toast({
        title: "Error",
        description: "Failed to update compensation",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Compensation</DialogTitle>
          <DialogDescription>
            Update compensation details for {compensation.employee.user.firstName} {compensation.employee.user.lastName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Compensation Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm font-medium">
                    Start Date *
                  </Label>
                  <Input
                    id="startDate"
                    type="date"
                    {...register("startDate")}
                    className="h-10"
                    value={watch("startDate") || ""}
                  />
                  {errors.startDate && (
                    <p className="text-sm text-red-600">{errors.startDate.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate" className="text-sm font-medium">
                    End Date *
                  </Label>
                  <Input
                    id="endDate"
                    type="date"
                    {...register("endDate")}
                    className="h-10"
                    value={watch("endDate") || ""}
                  />
                  {errors.endDate && (
                    <p className="text-sm text-red-600">{errors.endDate.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newSalary" className="text-sm font-medium">
                    New Salary *
                  </Label>
                  <Input
                    id="newSalary"
                    {...register("newSalary")}
                    placeholder="Enter new salary"
                    className="h-10"
                    value={watch("newSalary") || ""}
                  />
                  {errors.newSalary && (
                    <p className="text-sm text-red-600">{errors.newSalary.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="payFrequency" className="text-sm font-medium">
                    Pay Frequency *
                  </Label>
                  <Select
                    value={watch("payFrequency")}
                    onValueChange={(value) => setValue("payFrequency", value)}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select pay frequency" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Monthly">Monthly</SelectItem>
                      <SelectItem value="Weekly">Weekly</SelectItem>
                      <SelectItem value="Bi-weekly">Bi-weekly</SelectItem>
                      <SelectItem value="Quarterly">Quarterly</SelectItem>
                      <SelectItem value="Annually">Annually</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.payFrequency && (
                    <p className="text-sm text-red-600">{errors.payFrequency.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="noticePeriodInDays" className="text-sm font-medium">
                    Notice Period (Days) *
                  </Label>
                  <Input
                    id="noticePeriodInDays"
                    {...register("noticePeriodInDays")}
                    placeholder="Enter notice period in days"
                    className="h-10"
                    value={watch("noticePeriodInDays") || ""}
                  />
                  {errors.noticePeriodInDays && (
                    <p className="text-sm text-red-600">{errors.noticePeriodInDays.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notifyPeriodInDays" className="text-sm font-medium">
                    Notify Period (Days) *
                  </Label>
                  <Input
                    id="notifyPeriodInDays"
                    {...register("notifyPeriodInDays")}
                    placeholder="Enter notify period in days"
                    className="h-10"
                    value={watch("notifyPeriodInDays") || ""}
                  />
                  {errors.notifyPeriodInDays && (
                    <p className="text-sm text-red-600">{errors.notifyPeriodInDays.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="salaryMode" className="text-sm font-medium">
                    Salary Mode *
                  </Label>
                  <Select
                    value={watch("salaryMode")}
                    onValueChange={(value) => setValue("salaryMode", value)}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select salary mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Fixed">Bank Trasfer</SelectItem>
                      <SelectItem value="Variable">Cash</SelectItem>
                      <SelectItem value="Performance-based">Check</SelectItem>
                      <SelectItem value="Commission">Digital Payment</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.salaryMode && (
                    <p className="text-sm text-red-600">{errors.salaryMode.message}</p>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="note" className="text-sm font-medium">
                  Note
                </Label>
                <Textarea
                  id="note"
                  {...register("note")}
                  placeholder="Enter any additional notes"
                  className="min-h-[100px]"
                  value={watch("note") || ""}
                />
                {errors.note && (
                  <p className="text-sm text-red-600">{errors.note.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Updating..." : "Update Compensation"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

